/**
 * Die Schale der Anwendung: Navigation, Anmeldung, Warteschlangenanzeige.
 */

import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link, Navigate, Route, Routes, useLocation, useParams } from "react-router";
import type { Sitzung } from "@thw/contracts";
import {
  anfrage,
  geraeteKennung,
  sitzungLesen,
  sitzungLoeschen,
  sitzungSpeichern,
} from "./api/klient.js";
import { abgleichen, abgleichStarten, useLaufzustand } from "./lokal/abgleich.js";
import { useBestand, useLokal, useSyncZahlen } from "./lokal/bestand.js";
import { vorWieLange } from "./lokal/beschreiben.js";
import { metaLesen } from "./lokal/datenbank.js";
import { neueFassungEinspielen, useNeueFassung } from "./pwa/dienst.js";
import { lagerortNachCodeIn } from "./scan/aufloesen.js";
import { Knopf, Ladeanzeige, Rueckmeldung } from "./ui/bausteine.jsx";
import { Abgleich } from "./seiten/Abgleich.jsx";
import { ArtikelSeite } from "./seiten/ArtikelSeite.jsx";
import { Dashboard } from "./seiten/dashboard/Dashboard.jsx";
import { Konto } from "./seiten/Konto.jsx";
import { BenutzerVerwaltung } from "./seiten/verwaltung/BenutzerVerwaltung.jsx";
import { StatusVerwaltung } from "./seiten/verwaltung/StatusVerwaltung.jsx";
import { Archiv } from "./seiten/Archiv.jsx";
import { ScrollZurueck, ZurueckKnopf } from "./ui/navigation.jsx";
import { LagerortVerwaltung } from "./seiten/verwaltung/LagerortVerwaltung.jsx";
import { PackstueckTypVerwaltung } from "./seiten/verwaltung/PackstueckTypVerwaltung.jsx";
import { ImportSeite } from "./seiten/verwaltung/ImportSeite.jsx";
import { Geraeteliste } from "./seiten/Geraeteliste.jsx";
import { Texterkennung } from "./seiten/versuch/Texterkennung.jsx";
import { Baum } from "./seiten/Baum.jsx";
import { Etiketten } from "./seiten/Etiketten.jsx";
import { LagerortSeite } from "./seiten/LagerortSeite.jsx";
import { ScanEinstieg } from "./seiten/ScanEinstieg.jsx";
import { WoIst } from "./seiten/WoIst.jsx";

/**
 * Die Statusanzeige des Abgleichs (KONZEPT.md §15.2).
 *
 * Dauerhaft in der Kopfzeile, nicht versteckt: Wer offline gebucht hat, muss
 * jederzeit sehen können, dass noch etwas unterwegs ist. Vier Zustände --
 * **Online / synchron**, **Offline — N Buchungen warten**,
 * **Synchronisiere …**, **Fehler** -- und alle führen per Tipp zum
 * Sync-Protokoll.
 */
function Syncanzeige() {
  const lauf = useLaufzustand();
  const zahlen = useSyncZahlen();
  const zwischenspeicher = useQueryClient();
  const wartend = zahlen?.wartend ?? 0;

  // Ist die Warteschlange gerade leergelaufen, hat der Server neue Wahrheit.
  // Die Seiten aus der lokalen Kopie merken das von selbst; die wenigen, die
  // noch den Server fragen (Übersicht, Verwaltung), werden hier erneuert.
  const [vorher, setVorher] = useState(wartend);
  if (vorher !== wartend) {
    setVorher(wartend);
    if (vorher > 0 && wartend === 0) void zwischenspeicher.invalidateQueries();
  }

  const online = lauf.online && lauf.erreichbar;
  const fehler = lauf.fehler !== null || (zahlen?.abgelehnt ?? 0) > 0;
  const buchungen = `${String(wartend)} ${wartend === 1 ? "Buchung wartet" : "Buchungen warten"}`;

  let text: string;
  let klasse: string;
  if (fehler) {
    text = "Fehler";
    klasse = "bg-red-600 text-white";
  } else if (!online) {
    text = wartend > 0 ? `Offline — ${buchungen}` : "Offline";
    klasse = "bg-amber-400 text-neutral-900";
  } else if (lauf.laeuft && wartend > 0) {
    text = "Synchronisiere …";
    klasse = "bg-blue-600 text-white";
  } else if (wartend > 0) {
    text = buchungen;
    klasse = "bg-amber-400 text-neutral-900";
  } else {
    text = "Online / synchron";
    klasse = "text-emerald-200";
  }

  return (
    <span className="flex min-w-0 flex-col items-center">
      {/*
        Ein Tipp gleicht sofort ab, auch die Stammdaten, und zeigt das
        Protokoll (Rückmeldungen 7.2).
      */}
      <Link
        to="/abgleich"
        data-testid="syncanzeige"
        aria-label={`Abgleich: ${text}`}
        onClick={() => {
          void abgleichen({ stammdaten: true });
        }}
        className={`flex min-h-10 items-center gap-2 rounded-lg px-2 text-xs font-semibold ${klasse}`}
      >
        {text}
        {(zahlen?.hinweise ?? 0) > 0 && (
          <span className="rounded-full bg-white px-2 py-0.5 text-neutral-900">
            {zahlen?.hinweise} {zahlen?.hinweise === 1 ? "Hinweis" : "Hinweise"}
          </span>
        )}
      </Link>
      <ZuletztAbgeglichen />
    </span>
  );
}

/** „vor 2 Min.“ -- wann zuletzt abgeglichen wurde, jede halbe Minute neu. */
function ZuletztAbgeglichen() {
  const zuletzt = useLokal(() => metaLesen("letzter_abgleich"), []);
  const [jetzt, setJetzt] = useState(() => new Date());
  useEffect(() => {
    const uhr = window.setInterval(() => {
      setJetzt(new Date());
    }, 30_000);
    return () => {
      window.clearInterval(uhr);
    };
  }, []);
  if (zuletzt === undefined || zuletzt === null) return null;
  return (
    <span data-testid="zuletzt-abgeglichen" className="text-[11px] leading-none text-blue-100">
      {vorWieLange(zuletzt, jetzt)}
    </span>
  );
}

/**
 * Hinweis auf eine neue Fassung der App. Antippen spielt sie ein -- niemand
 * wird mitten im Scannen unterbrochen (CLAUDE.md §7).
 */
function NeueFassung() {
  if (!useNeueFassung()) return null;
  return (
    <button
      type="button"
      className="min-h-12 w-full bg-blue-950 px-4 text-sm font-semibold text-white"
      onClick={neueFassungEinspielen}
    >
      Neue Fassung bereit — antippen zum Aktualisieren
    </button>
  );
}

/**
 * Der erste Start auf einem Gerät: Ohne lokale Kopie gibt es nichts zu
 * zeigen. Das geschieht mit Netz, direkt nach der Anmeldung -- in der
 * Unterkunft, nicht an der Einsatzstelle.
 */
function ErsterAbgleich() {
  const lauf = useLaufzustand();
  return (
    <div className="mx-auto flex max-w-sm flex-col gap-4 p-6">
      <h1 className="text-2xl font-bold">Bestand wird geladen …</h1>
      <p className="text-neutral-600 dark:text-neutral-300">
        Das Gerät holt sich einmal den ganzen Bestand. Danach geht alles auch ohne Netz.
      </p>
      {lauf.geladen > 0 && (
        <p className="text-sm text-neutral-500">{lauf.geladen} Buchungen geladen</p>
      )}
      {lauf.fehler !== null && (
        <Rueckmeldung art="warnung" kopf="Das hat nicht geklappt" zusatz={lauf.fehler} />
      )}
      {!lauf.laeuft && (lauf.fehler !== null || !lauf.erreichbar) && (
        <>
          {!lauf.erreichbar && (
            <Rueckmeldung
              art="warnung"
              kopf="Kein Netz"
              zusatz="Für das erste Laden braucht das Gerät einmal Verbindung zum Server."
            />
          )}
          <Knopf art="haupt" onClick={() => void abgleichen({ stammdaten: true })}>
            Erneut versuchen
          </Knopf>
        </>
      )}
    </div>
  );
}

/**
 * Ein Lagerort-QR, mit der Kamera des Handys gescannt, öffnet `/l/K7M2Q`.
 * Der Service Worker liefert dafür die App aus -- auch ohne Netz --, und
 * hier wird der Kurzcode (auf alten Etiketten die Kennung) aus der lokalen
 * Kopie aufgelöst (ADR 0017).
 */
function KurzLink() {
  const { code = "" } = useParams();
  const bestand = useBestand();
  const lagerort = bestand === undefined ? undefined : lagerortNachCodeIn(bestand, code);
  // Unbekannt, aber Netz: vielleicht ist der Ort neuer als die lokale Kopie.
  const nachholen = lagerort === null && globalThis.navigator.onLine;
  useEffect(() => {
    if (nachholen) void abgleichen({ stammdaten: true });
  }, [nachholen, code]);
  if (lagerort === undefined) return <Ladeanzeige was="Der Lagerort" />;
  if (lagerort === null) {
    return (
      <Rueckmeldung
        art="warnung"
        kopf={`Kein Lagerort „${code}“`}
        zusatz={
          <Link to="/scannen" className="font-semibold underline">
            Zum Scannen
          </Link>
        }
      />
    );
  }
  return <Navigate to={`/lagerort/${lagerort.id}`} replace />;
}

function Anmeldung({ aufAngemeldet }: { aufAngemeldet: () => void }) {
  const [benutzername, setBenutzername] = useState("");
  const [geheimnis, setGeheimnis] = useState("");
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);

  const absenden = async (e: React.SyntheticEvent): Promise<void> => {
    e.preventDefault();
    setLaeuft(true);
    setFehler(null);
    try {
      const geraet = geraeteKennung();
      const sitzung = await anfrage<Sitzung>("POST", "/api/core/anmelden", {
        benutzername,
        geheimnis,
        geraet,
      });
      sitzungSpeichern(sitzung, geraet);
      aufAngemeldet();
    } catch (f) {
      setFehler(f instanceof Error ? f.message : "Anmeldung fehlgeschlagen");
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <form className="mx-auto flex max-w-sm flex-col gap-4 p-6" onSubmit={(e) => void absenden(e)}>
      <h1 className="text-2xl font-bold">Anmelden</h1>
      {fehler !== null && <Rueckmeldung art="warnung" kopf={fehler} />}

      <label className="flex flex-col gap-1">
        <span className="text-sm font-semibold">Benutzername</span>
        <input
          className="min-h-12 rounded-xl border-2 border-neutral-300 px-4 text-base dark:border-neutral-600 dark:bg-neutral-800 dark:text-white"
          value={benutzername}
          onChange={(e) => {
            setBenutzername(e.target.value);
          }}
          autoComplete="username"
          autoCapitalize="none"
        />
      </label>

      <label className="flex flex-col gap-1">
        <span className="text-sm font-semibold">Passwort oder PIN</span>
        <input
          type="password"
          className="min-h-12 rounded-xl border-2 border-neutral-300 px-4 text-base dark:border-neutral-600 dark:bg-neutral-800 dark:text-white"
          value={geheimnis}
          onChange={(e) => {
            setGeheimnis(e.target.value);
          }}
          autoComplete="current-password"
        />
      </label>

      <Knopf type="submit" art="haupt" disabled={laeuft}>
        {laeuft ? "…" : "Anmelden"}
      </Knopf>
      <p className="text-sm text-neutral-500">
        Die Anmeldung bleibt auf diesem Gerät bestehen — an der Einsatzstelle gibt es kein Netz, um
        sich neu anzumelden.
      </p>
    </form>
  );
}

export function App() {
  const [angemeldet, setAngemeldet] = useState(() => sitzungLesen() !== null);
  const ort = useLocation();
  // Einmal vollständig abgeglichen? Bis dahin gibt es keinen lokalen Bestand.
  const zuletzt = useLokal(() => metaLesen("letzter_abgleich"), []);

  useEffect(() => {
    if (!angemeldet) return;
    abgleichStarten();
    // Nach einer neuen Anmeldung gleich abgleichen -- die alte Sitzung kann
    // einen Fehler hinterlassen haben, der sonst bis zum naechsten Takt steht.
    void abgleichen();
  }, [angemeldet]);

  if (!angemeldet)
    return (
      <Anmeldung
        aufAngemeldet={() => {
          setAngemeldet(true);
        }}
      />
    );

  if (zuletzt === undefined) return null;
  if (zuletzt === null) return <ErsterAbgleich />;

  const sitzung = sitzungLesen();

  return (
    <div className="min-h-dvh bg-neutral-100 text-neutral-900 dark:bg-neutral-950 dark:text-neutral-50">
      <NeueFassung />
      <header className="sticky top-0 z-10 flex items-center justify-between gap-2 bg-blue-800 px-4 py-2 text-white">
        <ScrollZurueck />
        <span className="flex min-w-0 items-center gap-1">
          <ZurueckKnopf />
          <Link to="/" className="shrink-0 font-bold whitespace-nowrap">
            THW Material
          </Link>
        </span>
        <Syncanzeige />
        {/*
          Der Name fuehrt zum Konto -- nicht zum Abmelden. Ein versehentlicher
          Tipp an der Einsatzstelle haette sonst ausgesperrt, und ohne Netz
          kommt dort niemand wieder hinein.
        */}
        <Link
          to="/konto"
          className="flex min-h-12 max-w-[30%] min-w-0 items-center rounded-lg px-2 text-sm font-semibold active:bg-blue-900"
        >
          {sitzung?.benutzer.anzeigename ?? "Konto"}
        </Link>
      </header>

      <main className="mx-auto max-w-2xl p-4 pb-24">
        <Routes>
          <Route path="/" element={<WoIst />} />
          <Route path="/scannen" element={<ScanEinstieg />} />
          <Route path="/baum" element={<Baum />} />
          <Route path="/etiketten" element={<Etiketten />} />
          <Route path="/lagerort/:id" element={<LagerortSeite />} />
          <Route path="/artikel/:id" element={<ArtikelSeite />} />
          <Route path="/uebersicht" element={<Dashboard />} />
          <Route path="/abgleich" element={<Abgleich />} />
          <Route path="/archiv" element={<Archiv />} />
          <Route path="/geraete" element={<Geraeteliste />} />
          <Route path="/versuch/texterkennung" element={<Texterkennung />} />
          <Route path="/l/:code" element={<KurzLink />} />
          <Route
            path="/konto"
            element={
              <Konto
                abmelden={() => {
                  sitzungLoeschen();
                  setAngemeldet(false);
                }}
              />
            }
          />
          <Route path="/verwaltung/benutzer" element={<BenutzerVerwaltung />} />
          <Route path="/verwaltung/status" element={<StatusVerwaltung />} />
          <Route path="/verwaltung/lagerorte" element={<LagerortVerwaltung />} />
          <Route path="/verwaltung/packstuecktypen" element={<PackstueckTypVerwaltung />} />
          <Route path="/verwaltung/import" element={<ImportSeite />} />
          {/* Kommt jemand über eine Kurz-URL, hat der Server schon
              weitergeleitet. Alles andere landet auf der Suche. */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>

      {/*
        Die Fußleiste. Der Scan-Knopf ist der größte: Scannen ist der
        Haupteinstieg (KONZEPT.md §8).
      */}
      <nav className="fixed inset-x-0 bottom-0 flex justify-around border-t border-neutral-300 bg-white p-2 dark:border-neutral-700 dark:bg-neutral-900">
        <Link
          to="/uebersicht"
          className={`flex min-h-12 flex-1 items-center justify-center rounded-xl text-sm font-semibold ${ort.pathname === "/uebersicht" ? "bg-neutral-200 dark:bg-neutral-700" : ""}`}
        >
          Übersicht
        </Link>
        <Link
          to="/"
          className={`flex min-h-12 flex-1 items-center justify-center rounded-xl text-sm font-semibold ${ort.pathname === "/" ? "bg-neutral-200 dark:bg-neutral-700" : ""}`}
        >
          Wo ist …?
        </Link>
        <Link
          to="/scannen"
          className="mx-2 flex min-h-12 flex-[2] items-center justify-center rounded-xl bg-blue-700 text-base font-bold text-white"
        >
          Scannen
        </Link>
        <Link
          to="/baum"
          className={`flex min-h-12 flex-1 items-center justify-center rounded-xl text-sm font-semibold ${ort.pathname === "/baum" ? "bg-neutral-200 dark:bg-neutral-700" : ""}`}
        >
          Lager
        </Link>
      </nav>
    </div>
  );
}
